
export enum Role {
  ADMIN = 'ADMIN',
  GERANT = 'GERANT',
  COMMERCIAL = 'COMMERCIAL',
  COMPTABLE = 'COMPTABLE',
  MAGASINIER = 'MAGASINIER',
  TECHNICIEN = 'TECHNICIEN'
}

export enum MachineStatus {
  AVAILABLE = 'AVAILABLE',
  RENTED = 'RENTED',
  LOA = 'LOA',
  PCR = 'PCR',
  MAINTENANCE = 'MAINTENANCE',
  SOLD = 'SOLD',
  RESERVED = 'RESERVED'
}

export enum ContractType {
  RENTAL = 'RENTAL',
  LOA = 'LOA',
  SALE = 'SALE'
}

export enum DocStatus {
  DRAFT = 'DRAFT',
  PENDING_APPROVAL = 'PENDING_APPROVAL', // Submitted, awaiting manager approval
  VALIDATED = 'VALIDATED', // Contract validated/signed, awaiting payment
  CAUTION_PAYEE = 'CAUTION_PAYEE', // Deposit/apport paid, ready for departure
  ACTIVE = 'ACTIVE',       // Machine delivered/picked up
  CLOSED = 'CLOSED',       // Returned and finalized
  DEFAULTED = 'DEFAULTED', // LOA: consecutive unpaid installments
  CANCELLED = 'CANCELLED'
}

export enum ProductType {
  MACHINE = 'MACHINE',
  PART = 'PART',
  CONSUMABLE = 'CONSUMABLE'
}

export enum MovementType {
  IN = 'ENTREE',
  OUT = 'SORTIE',
  TRANSFER = 'TRANSFERT',
  ADJUSTMENT = 'AJUSTEMENT'
}

export enum ClientType {
  INDIVIDUAL = 'PARTICULIER',
  COMPANY = 'ENTREPRISE',
  COOPERATIVE = 'COOPERATIVE',
  GOVERNMENT = 'ETAT_ONG'
}

export enum QuoteStatus {
  DRAFT = 'DRAFT',
  SENT = 'SENT',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  EXPIRED = 'EXPIRED',
  CONVERTED = 'CONVERTED',
  CANCELLED = 'CANCELLED'
}

export enum InvoiceStatus {
  DRAFT = 'DRAFT',
  VALIDATED = 'VALIDATED', // Definitive
  PARTIAL = 'PARTIAL',
  PAID = 'PAID',
  OVERDUE = 'OVERDUE',
  CANCELLED = 'CANCELLED'
}

export enum PaymentMethod {
  CASH = 'ESPECES',
  CHECK = 'CHEQUE',
  TRANSFER = 'VIREMENT',
  MOBILE_MONEY_MTN = 'MOBILE_MONEY_MTN',
  MOBILE_MONEY_MOOV = 'MOBILE_MONEY_MOOV',
}

export interface QuoteItem {
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  discount: number; // percentage
  total: number;
  /** Invoice line id — links a BL line back to the invoice line (partial delivery). */
  factureLigneId?: string;
  /** Quantity already delivered on prior non-cancelled BLs. */
  quantityDelivered?: number;
  /** Remaining quantity still to deliver. */
  quantityRemaining?: number;
}

export interface Quote {
  id: string; // DEV-2024-xxx
  clientId: string;
  clientName: string;
  date: string;
  validUntil: string;
  commercialId: string;
  commercialName: string;
  items: QuoteItem[];
  subtotal: number;
  tax: number;
  total: number;
  status: QuoteStatus;
  notes?: string;
}

export interface InvoicePayment {
  id: string;
  date: string;
  amount: number;
  method: PaymentMethod;
  reference: string;
  recordedBy: string;
}

export interface Invoice {
  id: string; // FAC-2024-xxx
  quoteId?: string;
  clientId: string;
  clientName: string;
  type: 'PROFORMA' | 'DEFINITIVE' | 'AVOIR';
  date: string;
  dueDate: string;
  items: QuoteItem[];
  subtotal: number;
  tax: number;
  total: number;
  paidAmount: number;
  remainingAmount: number;
  status: InvoiceStatus;
  payments: InvoicePayment[];
}

export interface DeliveryNote {
  id: string; // BL-2024-xxx
  invoiceId: string;
  clientId: string;
  date: string;
  items: { productId: string; productName: string; quantityOrdered: number; quantityDelivered: number }[];
  status: 'PENDING' | 'DELIVERED';
  clientSignature?: string;
  driverSignature?: string;
}

export interface Machine {
  id: string;
  name: string;
  serial: string;
  category: string;
  status: MachineStatus;
  location: string;
  dailyRate: number;
  purchasePrice: number;
  imageUrl: string;
  stockTotal?: number;
}

export interface Product {
  id: string;
  reference: string;
  name: string;
  category: string;
  type: ProductType;
  isSerialized: boolean;
  minStockThreshold: number;
  stockTotal: number;
  stockBySite: Record<string, number>; // e.g., { 'Cotonou': 5, 'Parakou': 2 }
  priceSale: number;
  priceRental: number; // Daily rate if applicable
  purchasePrice: number;
  imageUrl?: string;
  status: 'OK' | 'CRITICAL';
  description?: string;
}

export interface StockMovement {
  id: string;
  date: string;
  productId: string;
  productName: string;
  type: MovementType;
  quantity: number;
  siteSource: string;
  siteDestination?: string; // For transfers
  user: string;
  referenceDoc: string; // BL number, Invoice number, etc.
}

export interface Client {
  id: string;
  code: string; // e.g. CL-2024-001
  name: string; // Contact person name
  company?: string; // Company name if applicable
  type: ClientType;
  provenance: string; // Region or source
  email: string;
  phone: string;
  address: string;
  city: string;
  isVatExempt: boolean;
  isActive: boolean;
  solvencyScore: number; // 0-100
}

export interface LoaInstallment {
  id: number;
  dueDate: string;
  amount: number;
  interestPart: number;
  principalPart: number;
  status: 'PENDING' | 'PAID' | 'OVERDUE' | 'PARTIAL';
  paidAmount: number;
  paymentDate?: string;
}

export interface Contract {
  id: string;
  type: ContractType;
  clientId: string;
  clientName?: string; // Helper for display
  machineId: string;
  machineName?: string; // Helper for display
  startDate: string;
  endDate: string;
  status: DocStatus;
  totalAmount: number;
  depositAmount: number;
  depositStatus: 'PENDING' | 'PAID' | 'REFUNDED' | 'PARTIAL_REFUND';
  cautionRestituee?: boolean;
  entryReportId?: string;
  exitReportId?: string;
  
  // LOA Specifics
  loaDurationMonths?: number;
  loaInterestRate?: number; // Annual percentage
  loaResidualValue?: number; // Option d'achat
  installments?: LoaInstallment[];
}

export interface AuditLog {
  id: string;
  timestamp: string;
  user: string;
  action: string;
  details: string;
}

export interface ConditionReport {
  id: string;
  contractId: string;
  type: 'ENTRY' | 'EXIT';
  date: string;
  photos: string[]; // URLs
  checklist: Record<string, boolean>; // e.g., { lights: true, tires: true }
  agentSignature: string | null;
  clientSignature: string | null;
  comments?: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: Role;
  status: 'ACTIVE' | 'INACTIVE';
  lastLogin?: string;
  avatar?: string;
}

export interface SystemSettings {
  vatRate: number;
  currency: string;
  minDepositPercent: number;
  lowStockThresholdGlobal: number;
  prefixes: {
    invoice: string;
    quote: string;
    contract: string;
    delivery: string;
  };
}

export interface CompanySettings {
  id: string;
  raison_sociale: string;
  forme_juridique?: string;
  capital_social?: string;
  adresse: string;
  ville?: string;
  pays?: string;
  telephone: string;
  email: string;
  site_web?: string;
  ifu: string;
  rccm: string;
  logo_url?: string;
  signature_url?: string;
  tva_default: number;
  min_deposit_percent: number;
  currency: string;
  low_stock_threshold: number;
  prefix_invoice: string;
  prefix_quote: string;
  prefix_contract: string;
  prefix_loa: string;
  prefix_delivery: string;
  prefix_movement: string;
  prefix_client: string;
  mentions_legales?: string;
  created_at: string;
  updated_at: string;
}

export interface ProductTypeRef {
  id: string;
  libelle: string;
  base_type: 'MACHINE' | 'PART' | 'CONSUMABLE';
  description?: string;
  is_active: boolean;
  ordre: number;
}
